import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

// Click-to-sort for every table in the app, and the one colour their headers
// share.
//
// A sort is { key, dir, value }: the column clicked, 'asc' or 'desc', and how
// to read that column's value from a row. Clicking a column sorts it
// ascending, again descending, a third time back to the list's own order.

// The header row's colour: one solid colour for every table (index.css
// .table-header), with white text.
export const TABLE_HEAD_ROW = 'table-header';
export const TABLE_HEAD_TEXT = 'text-white text-[11px] font-semibold uppercase tracking-wider';
// Tables have no frame on the left and right; a line under every row
// (the last included) divides them.
// Dark enough to show on the light grey of alternate rows, not only on hover.
export const TABLE_ROW_LINE = 'border-b border-gray-300 dark:border-slate-600';

const isBlank = (v) => v === null || v === undefined || v === '' || v === '-';

// Amounts as shown (N$1,234.50, 12.5%) and as stored (1234.50) read as numbers.
const AMOUNT = /^-?\s*(?:N\$|NAD)?\s*-?[\d,]*\d(?:\.\d+)?\s*%?$/i;
// A date as the app shows it, dd/mm/yyyy with an optional time.
const DMY = /^(\d{2})\/(\d{2})\/(\d{4})(?:[ ,T]+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/;

// One value in a form that compares the way people read it: a number, or
// text, with shown dates turned into yyyy-mm-dd so they sort by day like the
// ISO dates the API sends.
const normalise = (v) => {
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  const s = String(v).trim();
  if (AMOUNT.test(s) && /\d/.test(s)) {
    const n = Number(s.replace(/N\$|NAD|[,%\s]/gi, ''));
    if (Number.isFinite(n)) return n;
  }
  const d = s.match(DMY);
  if (d) {
    const [, dd, mm, yyyy, h = '0', mi = '00', ss = '00'] = d;
    return `${yyyy}-${mm}-${dd}T${h.padStart(2, '0')}:${mi}:${ss}`;
  }
  return s;
};

// Numbers by size, dates by day and time, true before false, text
// alphabetically with the numbers in it read as numbers. Blanks go last
// whichever way the column is sorted (sortRows).
export const compareValues = (a, b) => {
  const x = normalise(a);
  const y = normalise(b);
  if (typeof x === 'boolean' || typeof y === 'boolean') return Number(Boolean(y)) - Number(Boolean(x));
  if (typeof x === 'number' && typeof y === 'number') return x - y;
  return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
};

export const sortRows = (rows, sort) => {
  const list = Array.isArray(rows) ? rows : [];
  if (!sort || typeof sort.value !== 'function') return list;
  const sign = sort.dir === 'desc' ? -1 : 1;
  return list
    .map((row, index) => ({ row, index, v: sort.value(row) }))
    .sort((x, y) => {
      const bx = isBlank(x.v);
      const by = isBlank(y.v);
      if (bx || by) return bx === by ? x.index - y.index : (bx ? 1 : -1);
      return sign * compareValues(x.v, y.v) || x.index - y.index;
    })
    .map((x) => x.row);
};

// The next sort after a click on column `key`.
export const nextSort = (current, key, value) => {
  if (!current || current.key !== key) return { key, dir: 'asc', value };
  if (current.dir === 'asc') return { key, dir: 'desc', value };
  return null;
};

// Sort state plus the sorted rows. A list that is split into pages sorts the
// whole list with this before taking a page, and hands `sort` and
// `onSortChange` to its table, so a click orders every page, not just one.
export const useTableSort = (rows) => {
  const [sort, setSort] = useState(null);
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  return { rows: sorted, sort, onSortChange: setSort };
};

// A clickable column header. `sort` is the table's current sort, `onSort`
// is called with the column's key and value reader.
export const SortTh = ({ label, sortKey, value, sort, onSort, className = '', style, align = 'left' }) => {
  const sortable = Boolean(onSort && sortKey && label);
  const active = sortable && sort?.key === sortKey;
  const ariaSort = active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined;
  const Icon = active ? (sort.dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className={`${className} ${align === 'right' ? 'text-right' : 'text-left'}`} style={style} aria-sort={ariaSort} scope="col">
      {sortable ? (
        <button
          type="button"
          onClick={() => onSort(sortKey, value)}
          title={`Sort by ${typeof label === 'string' ? label : 'this column'}`}
          className={`inline-flex items-center gap-1 uppercase tracking-wider font-semibold hover:underline focus:outline-none focus-visible:underline ${align === 'right' ? 'flex-row-reverse' : ''}`}
        >
          <span>{label}</span>
          <Icon size={12} className={active ? 'opacity-100' : 'opacity-50'} aria-hidden="true" />
        </button>
      ) : label}
    </th>
  );
};

// Header cells wired to a sort held in state: `th(label, key, value, extra)`.
// For a table built inside render code, where a hook cannot be called: keep
// the sort in the component's state and sort with sortRows.
export const makeSortTh = (sort, setSort) => (label, key, value, { className = '', align = 'left', style } = {}) => (
  <SortTh
    key={key}
    label={label}
    sortKey={key}
    value={value}
    sort={sort}
    onSort={(k, v) => setSort(nextSort(sort, k, v))}
    className={className}
    align={align}
    style={style}
  />
);

// For a hand-built table: the sort state, the sorted rows and header cells
// already wired to them.
export const useSortableTable = (rows) => {
  const { rows: sorted, sort, onSortChange } = useTableSort(rows);
  return { rows: sorted, sort, onSortChange, th: makeSortTh(sort, onSortChange) };
};
